package com.example.mystybag.login.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import dev.icerock.moko.resources.compose.stringResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.BottomSheetUIView
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebPortraitLayout
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.helpers.navigateToLoginHelp
import com.example.mystybag.login.views.components.WebInlineLanguageSwitcher
import com.example.mystybag.login.logic.EnterEmailViewModel
import androidx.compose.material3.Text
import androidx.compose.ui.graphics.Color
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.core.ui.theme.LocalTheme

private val WELCOME_LOGO_MARK: String
    get() = "https://$DOMAIN/static/email_photos/logo_40.png"

@Composable
fun EnterEmailView(viewModel: EnterEmailViewModel) {
    if (PlatformHelper.isWeb && !rememberIsWebPortraitLayout()) {
        EnterEmailViewWeb(viewModel)
        return
    }

    val keyboardController = LocalSoftwareKeyboardController.current

    Box(modifier = Modifier.fillMaxSize()) {
        BottomSheetUIView(
            viewModel = viewModel,
            showTopBar = true,
            bottomSheetFactory = {}
        ) { uiState ->
            Column(
                modifier            = Modifier
                    .fillMaxWidth()
                    .weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState()),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Texts.DisplaySmall(
                        text = stringResource(SR.strings.sign_in),
                        modifier = Modifier.fillMaxWidth().padding(start = p16, top = p32),
                        textAlign = TextAlign.Start,
                        color = AppTheme.current.colors.onSurface,
                    )

                    if (uiState.value.description.isNotEmpty())
                        Texts.DisplaySmall(
                            text        = uiState.value.description,
                            textAlign   = TextAlign.Center,
                            modifier    = Modifier.padding(p8)
                        )

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = 56.dp)
                            .padding(top = p24, start = p16, end = p16)
                    ) {
                        DefaultCircleShapedTextField(
                            modifier        = Modifier.fillMaxWidth(),
                            value           = uiState.value.email,
                            label           = stringResource(SR.strings.email),
                            errorText       = uiState.value.emailErrorMsg,
                            onValueChange   = viewModel::onEmailChange,
                            isError         = uiState.value.emailIsError,
                            inputType       = TextFieldType.EMAIL,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email, imeAction = ImeAction.Done)
                        )
                    }
                }

                DefaultButton(
                    modifier   = Modifier
                        .fillMaxWidth()
                        .padding(bottom = p8, start = p16, end = p16),
                    caption    = stringResource(SR.strings.next),
                    callback   = {
                        keyboardController?.hide()
                        viewModel.onNextClicked()
                    },
                    identifier = "Login Screen Next"
                )
            }
        }
    }
}

@Composable
private fun EnterEmailViewWeb(viewModel: EnterEmailViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val theme = AppTheme.current
    val uiState = viewModel.uiState.subscribeAsState()

    Box(modifier = Modifier.fillMaxSize()) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier.width(530.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(48.dp)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(53.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Texts.DisplaySmall(
                        text = stringResource(SR.strings.sign_in),
                        color = theme.colors.onSurface,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                DefaultCircleShapedTextField(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.dp),
                    value = uiState.value.email,
                    label = stringResource(SR.strings.email),
                    errorText = uiState.value.emailErrorMsg,
                    onValueChange = viewModel::onEmailChange,
                    isError = uiState.value.emailIsError,
                    inputType = TextFieldType.EMAIL,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Email,
                        imeAction = ImeAction.Done
                    )
                )

                DefaultButton(
                    modifier = Modifier.fillMaxWidth(),
                    caption = stringResource(SR.strings.next),
                    callback = {
                        keyboardController?.hide()
                        viewModel.onNextClicked()
                    },
                    identifier = "Login Screen Next Web"
                )
            }
        }

        Box(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .fillMaxWidth()
                .height(120.dp)
                .background(theme.colors.surface),
            contentAlignment = Alignment.Center
        ) {
            Row(
                modifier = Modifier
                    .width(530.dp)
                    .padding(vertical = 32.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    modifier = Modifier,
                    horizontalArrangement = Arrangement.spacedBy(20.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(48.dp)
                            .clip(CircleShape)
                            .background(theme.colors.surfaceContainerLowest)
                            .noRippleClickable { viewModel.onBackButtonClicked() },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.arrow_back),
                            modifier = Modifier.size(18.dp)
                        )
                    }
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AsyncImage(
                            link = WELCOME_LOGO_MARK,
                            placeholder = null,
                            modifier = Modifier
                                .width(40.dp)
                                .height(40.dp)
                                .clip(RoundedCornerShape(10.dp)),
                            contentScale = ContentScale.Fit
                        )
                        Text(
                            text = "MystyBag",
                            color = theme.colors.onSurface,
                            fontFamily = LocalTheme.googleSans,
                            fontSize = 24.sp,
                            lineHeight = 40.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                Row(
                    horizontalArrangement = Arrangement.spacedBy(24.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Texts.BodyMedium(
                        text = "Help",
                        color = theme.colors.onSurfaceVariant,
                        modifier = Modifier.noRippleClickable { viewModel.navigateToLoginHelp() }
                    )
                    WebInlineLanguageSwitcher()
                }
            }
        }
    }
}


